import { toast } from 'vue3-toastify';

import type { Profile } from '@/interfaces/agency/auth';
import { disconnectSocket } from '@/plugins/socket';
import { useAuthStore } from '@/stores/agency/auth';
import { useUserManagementStore } from '@/stores/agency/settings/user-management/user-management';

const FOREIGN_LOGOUT_FLAG = 'foreignSessionLogout';

/**
 * Returns whether this tab has been forced out by a login from another tab.
 * Backed by sessionStorage (per-tab), so it never affects the tab that actually
 * owns the current localStorage session.
 */
export const isForeignSessionLogout = (): boolean => sessionStorage.getItem(FOREIGN_LOGOUT_FLAG) === '1';

export const clearForeignSessionLogoutFlag = (): void => {
  sessionStorage.removeItem(FOREIGN_LOGOUT_FLAG);
};

/**
 * localStorage is shared across every tab of the same browser, but each tab keeps its
 * own in-memory Pinia state. If a different account logs in from another tab it silently
 * overwrites the shared accessToken/userData, so this tab would otherwise keep running
 * with a token that now belongs to someone else — eventually failing a role/permission
 * check and landing on /not-authorized instead of being logged out.
 *
 * The `storage` event only fires in tabs OTHER than the one that made the change, so it's
 * a reliable signal that "someone else just changed the shared session." We key off the
 * user id inside `userData` (not accessToken) so a normal silent token refresh for the same
 * user never trips this.
 */
export const initCrossTabSessionWatcher = (): void => {
  window.addEventListener('storage', event => {
    if (event.key !== 'userData' || !event.newValue) {
      return;
    }

    const authStore = useAuthStore();
    const currentUserId = authStore.loggedInUserProfile?.id;

    if (!currentUserId) {
      return;
    }

    let newUserId: number | undefined;

    try {
      newUserId = JSON.parse(event.newValue)?.id;
    } catch {
      return;
    }

    if (!newUserId || newUserId === currentUserId) {
      return;
    }

    disconnectSocket();

    authStore.$patch({
      isAuthenticated: false,
      loggedInUserProfile: {} as Profile,
      activeTenantData: null,
      activeRoleName: '',
      activeTenantSlug: ''
    });

    useUserManagementStore().$patch({
      usersPermissionById: [],
      permissionCache: new Map()
    });

    sessionStorage.setItem(FOREIGN_LOGOUT_FLAG, '1');

    toast.info('You have been logged out because another account signed in from a different tab.');

    // Full navigation so this tab drops its stale in-memory state entirely, mirroring
    // authStore.logout()'s reasoning for using location.href instead of router.push.
    window.location.href = '/';
  });
};
